import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import permissionConfig from '../../../config/permissionConfig';
import { useSnackbar } from '../../providers/SnackbarProvider';
import { useDataProvider } from '../../providers/DataProvider';
import { useUserAccess } from '../../providers/UserAccessProvider';
import SnackbarApiErrorMessage from './SnackbarApiErrorMessage';

function truncateString(str, maxLength = 20) {
  if (typeof str !== 'string') return str;
  if (str.length <= maxLength) return str;
  return `${str.slice(0, 8)}...${str.slice(-6)}`;
}

function Breadcrumb({ items = [], apiUrl = null, nameKey = null }) {
  const userAccess = useUserAccess();
  const [breadcrumbItems, setBreadcrumbItems] = useState(items);
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get } = useDataProvider(provider);
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(true);

  const fetchEntity = async () => {
    setLoading(true);
    const response = await get(apiUrl);
    if (response?.ok) {
      const entityValue = response?.data?.[nameKey];
      const newItems = items;

      newItems[items.length - 1] = {
        ...items[items.length - 1],
        name: entityValue,
      };
      setBreadcrumbItems(newItems);
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (nameKey) {
      fetchEntity();
    } else {
      setBreadcrumbItems(items);
      setLoading(false);
    }
  }, [JSON.stringify(items), apiUrl, nameKey]);

  if (loading) {
    return <CircularProgress sx={{ mb: 1 }} size={20} />;
  }

  return (
    <Breadcrumbs sx={{ display: { xs: 'none', md: 'flex' } }}>
      {breadcrumbItems.map((item, index) => {
        const Icon = item.icon;
        const IconComponent = <Icon fontSize="inherit" size="12px" />;
        const hasPermission = item?.permissionName
          ? permissionConfig({
              userAccess,
              permissionName: item?.permissionName,
            })
          : true;

        if (index !== breadcrumbItems.length - 1 && hasPermission) {
          return (
            <Tooltip arrow title={item.name} key={item.name}>
              <Button
                disabled={!hasPermission}
                startIcon={<Icon />}
                variant="text"
                onClick={item.onClick}>
                {truncateString(item.name)}
              </Button>
            </Tooltip>
          );
        }
        return (
          <Typography
            gap={0.3}
            key={item.name}
            sx={{
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              fontSize: '14px',
              fontWeight: 500,
            }}>
            {IconComponent} {item.name}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
}

export function PublicBreadcrumb({ items }) {
  const breadcrumbItems = items;

  return (
    <Breadcrumbs sx={{ display: { xs: 'none', md: 'flex' } }}>
      {breadcrumbItems.map((item, index) => {
        const Icon = item.icon;
        let IconComponent = null;
        if (Icon) {
          IconComponent = <Icon fontSize="inherit" size="12px" />;
        }

        if (index !== breadcrumbItems.length - 1) {
          return (
            <Tooltip arrow title={item.name} key={item.name}>
              <Button
                {...(item.icon ? { startIcon: <Icon /> } : {})}
                variant="text"
                onClick={item.onClick}>
                {truncateString(item.name)}
              </Button>
            </Tooltip>
          );
        }

        return (
          <Typography
            gap={0.3}
            key={item.name}
            sx={{
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              fontSize: '14px',
              fontWeight: 500,
            }}>
            {IconComponent} {truncateString(item.name, 50)}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
}

export default Breadcrumb;
